import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiPaintBucket, FiCheckCircle, FiArrowRight, FiEye, FiShield, FiStar } = FiIcons;

function Maler() {
  const services = [
    {
      title: 'Innenanstriche aller Art',
      description: 'Professionelle Wandgestaltung mit hochwertigen Farben und Techniken.',
      features: ['Dispersionsfarben', 'Latexfarben', 'Naturfarben', 'Spezialfarben'],
      image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&h=400&fit=crop'
    },
    {
      title: 'Tapezierarbeiten',
      description: 'Fachgerechtes Tapezieren mit verschiedenen Tapetenarten.',
      features: ['Vliestapeten', 'Papiertapeten', 'Strukturtapeten', 'Designtapeten'],
      image: 'https://mx.ausbau.nrw/img/malerarbeiten/tapezieren.jpg'
    },
    {
      title: 'Spachteltechniken',
      description: 'Kreative Oberflächengestaltung mit modernen Spachteltechniken.',
      features: ['Glättspachtel', 'Strukturspachtel', 'Dekorspachtel', 'Kalkspachtel'],
      image: 'https://mx.ausbau.nrw/img/malerarbeiten/spachtelarbeit.jpg'
    },
    {
      title: 'Holzschutz und Lasuren',
      description: 'Schutz und Verschönerung von Holzoberflächen im Innenbereich.',
      features: ['Holzlasuren', 'Holzöle', 'Klarlacke', 'Farbige Lacke'],
      image: 'https://mx.ausbau.nrw/img/malerarbeiten/holzanstrich.jpg'
    }
  ];

  const techniques = [
    { icon: FiEye, title: 'Wischtechnik', description: 'Lebendige Oberflächenstrukturen durch spezielle Wischtechniken.' },
    { icon: FiStar, title: 'Schablonentechnik', description: 'Individuelle Muster und Designs mit Schablonen.' },
    { icon: FiShield, title: 'Schwammtechnik', description: 'Natürliche Strukturen durch Schwammauftrag.' }
  ];

  const colors = [
    { name: 'Weiß & Cremetöne', description: 'Klassisch und zeitlos für alle Räume' },
    { name: 'Erdtöne', description: 'Warme Braun- und Beigetöne für Gemütlichkeit' },
    { name: 'Grautöne', description: 'Modern und elegant für zeitgemäße Räume' },
    { name: 'Pastellfarben', description: 'Sanfte Farben für entspannte Atmosphäre' },
    { name: 'Kräftige Akzente', description: 'Mutige Farben für individuelle Gestaltung' },
    { name: 'Naturfarben', description: 'Ökologische Farben für gesundes Wohnen' }
  ];

  const advantages = [
    'Kostenlose Farbberatung vor Ort',
    'Hochwertige Markenfarben und -materialien',
    'Saubere und ordentliche Arbeitsweise',
    'Abdeckung und Schutz der Möbel',
    'Termingerechte Fertigstellung',
    'Nachbehandlung und Garantie'
  ];

  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center space-x-2 text-sm text-gray-600 mb-8"
        >
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900">Maler- und Beschichtungsarbeiten</span>
        </motion.nav>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiPaintBucket} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Maler- und Beschichtungsarbeiten
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professionelle Malerarbeiten und kreative Oberflächengestaltung für Wände, Decken und Holzelemente. Verleihen Sie Ihren Räumen neuen Glanz und Persönlichkeit.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <img
            src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=1200&h=600&fit=crop"
            alt="Maler- und Beschichtungsarbeiten"
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Techniques Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Kreative Maltechniken
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {techniques.map((technique, index) => (
              <motion.div
                key={technique.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={technique.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {technique.title}
                </h3>
                <p className="text-gray-600">
                  {technique.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Services Detail */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unsere Malerleistungen im Detail
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-mk-teal-500" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Color Palette */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-gray-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Farbwelten für jeden Geschmack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors.map((color, index) => (
              <motion.div
                key={color.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {color.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {color.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advantages Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-mk-teal-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Warum MK-AUSBAU für Ihre Malerarbeiten?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage, index) => (
              <motion.div
                key={advantage}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="flex items-center space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-mk-teal-600" />
                <span className="text-gray-700 font-medium">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Von der Farbberatung bis zum fertigen Anstrich
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Farbberatung', description: 'Individuelle Beratung zu Farben und Techniken' },
              { step: '2', title: 'Vorbereitung', description: 'Abkleben, Abdecken und Untergrundvorbereitung' },
              { step: '3', title: 'Ausführung', description: 'Professioneller Anstrich mit hochwertigen Materialien' },
              { step: '4', title: 'Fertigstellung', description: 'Reinigung und finale Qualitätskontrolle' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Bereit für frische Farben?
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Lassen Sie uns Ihre Räume in neuem Glanz erstrahlen. Von der Farbberatung bis zum perfekten Anstrich - wir sind Ihr Partner!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Farbberatung buchen</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Services Navigation Footer */}
      <ServicesNavigationFooter />

      {/* Google Maps Section */}
      <GoogleMaps />
    </div>
  );
}

export default Maler;